import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatTitle } from "@/lib/typography";
import { Glossaire } from "./Glossaire";
import { CopyButton } from "./CopyButton";
import { z } from "zod";

const reviewSchema = z.object({
  ctr: z.number().min(0).max(100),
  cvr: z.number().min(0).max(100),
  acos: z.number().min(0).max(100),
  cpc: z.number().min(0),
  beAcos: z.number().min(0).max(100),
});

interface Action {
  symptom: string;
  kpi: string;
  action: string;
  impact: string;
  deadline: string;
}

export const ReviewPage = () => {
  const [ctr, setCtr] = useState(0.35);
  const [cvr, setCvr] = useState(6.2);
  const [acos, setAcos] = useState(38);
  const [cpc, setCpc] = useState(0.48);
  const [beAcos, setBeAcos] = useState(28);
  const [actions, setActions] = useState<Action[]>([]);

  const run = () => {
    try {
      reviewSchema.parse({ ctr, cvr, acos, cpc, beAcos });
      const outs: Action[] = [];

      if (ctr < 0.3) {
        outs.push({
          symptom: "CTR <0,3 %",
          kpi: "CTR",
          action: "Revoir pertinence (mots-clés/ASIN) OU créa SB/SD (nouvelle vidéo/visuel)",
          impact: "Moyen",
          deadline: "7 jours",
        });
      }
      if (cvr < 6) {
        outs.push({
          symptom: "CVR <6 %",
          kpi: "CVR",
          action: "Travailler fiche produit (image principale, A+, avis) et prix",
          impact: "Élevé",
          deadline: "14 jours",
        });
      }
      if (acos > beAcos * 1.2) {
        outs.push({
          symptom: `ACoS > BE ×1,2`,
          kpi: "ACoS",
          action: "Baisser bids −10 à −15 % et ajouter négatifs",
          impact: "Élevé",
          deadline: "3 jours",
        });
      }
      if (acos < beAcos * 0.7) {
        outs.push({
          symptom: "ACoS <0,7× BE",
          kpi: "ACoS",
          action: "+10 à +20 % sur winners; augmenter Top-of-Search",
          impact: "Élevé",
          deadline: "5 jours",
        });
      }
      if (cpc > 0.8) {
        outs.push({
          symptom: "CPC élevé",
          kpi: "CPC",
          action: "Isoler requêtes chères, tester SB Video (souvent CPC plus bas en FR)",
          impact: "Moyen",
          deadline: "10 jours",
        });
      }
      if (outs.length === 0) {
        outs.push({
          symptom: "Configuration saine",
          kpi: "Tous",
          action: "Continuer le scaling progressif (+10 à +15 %) et AB-test créas",
          impact: "Faible",
          deadline: "Continu",
        });
      }

      setActions(outs);
    } catch (err) {
      console.error("Validation error:", err);
    }
  };

  const summary = actions.length > 0 ? `${actions.length} action(s) recommandée(s) pour optimiser les campagnes.` : "";

  const exportText = [
    "=== RÉSUMÉ EXÉCUTIF ===",
    summary,
    "",
    "=== ACTIONS RECOMMANDÉES ===",
    ...actions.map((a) => `- ${a.symptom} — ${a.action} (Impact ${a.impact}, Échéance ${a.deadline})`),
  ].join("\n");

  const glossaire = [
    { term: "Résumé exécutif", definition: "Vue d'ensemble des résultats et actions clés" },
    { term: "Harvest", definition: "Transfert des requêtes gagnantes vers Manuel" },
    { term: "Pruning", definition: "Suppression des requêtes non performantes" },
    { term: "SoV", definition: "Share of Voice — part de voix" },
    { term: "NTB%", definition: "Pourcentage de nouveaux acheteurs" },
    { term: "Placements", definition: "Emplacements publicitaires — Top of Search, Product Pages, etc." },
  ];

  return (
    <div className="space-y-4">
      {/* 1) REVUE HEBDO IA */}
      <Card>
        <CardHeader>
          <CardTitle>{formatTitle("1) Revue hebdo IA — entrées et seuils")}</CardTitle>
          <CardDescription>Saisie rapide (KPIs hebdo)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <label className="text-sm">
              CTR %
              <input type="number" step="0.01" value={ctr} onChange={(e) => setCtr(parseFloat(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              CVR %
              <input type="number" step="0.01" value={cvr} onChange={(e) => setCvr(parseFloat(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              ACoS %
              <input type="number" step="0.01" value={acos} onChange={(e) => setAcos(parseFloat(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              CPC €
              <input type="number" step="0.01" value={cpc} onChange={(e) => setCpc(parseFloat(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
            <label className="text-sm">
              Break-even ACoS %
              <input type="number" step="0.01" value={beAcos} onChange={(e) => setBeAcos(parseFloat(e.target.value || "0"))} className="mt-1 w-full rounded-lg border px-3 py-1.5 bg-input text-foreground" />
            </label>
          </div>
          <Button onClick={run} variant="outline">Diagnostiquer</Button>
        </CardContent>
      </Card>

      {/* 2) SORTIES ET ACTIONS */}
      {actions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{formatTitle("2) Sorties et actions (tableau décisionnel)")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-4">Symptôme</th>
                    <th className="py-2 pr-4">KPI</th>
                    <th className="py-2 pr-4">Action</th>
                    <th className="py-2 pr-4">Impact attendu</th>
                    <th className="py-2 pr-4">Échéance</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((a, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 text-muted-foreground">{a.symptom}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{a.kpi}</Badge>
                      </td>
                      <td className="py-2 pr-4">{a.action}</td>
                      <td className="py-2 pr-4">{a.impact}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{a.deadline}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3) LIVRABLE */}
      {actions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{formatTitle("3) Livrable")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="mb-3">
              <div className="text-sm font-semibold mb-1">{formatTitle("Résumé exécutif")}</div>
              <p className="text-sm text-muted-foreground">{summary}</p>
            </div>

            <div className="flex gap-2">
              <CopyButton text={exportText} label="Exporter rapport" />
            </div>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground border">
              {exportText}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* 4) GLOSSAIRE */}
      <Glossaire entries={glossaire} />
    </div>
  );
};
